import api from './api';

const customerService = {
  /**
   * Get all customers with optional pagination and filters
   * @param {Object} options - Options for fetching customers
   * @param {number} options.page - Page number (1-based)
   * @param {number} options.limit - Number of items per page
   * @param {Object} options.filters - Filter criteria
   * @param {string} [options.filters.company_name] - Filter by company name (case-insensitive)
   * @param {string} [options.filters.contact_name] - Filter by contact name (case-insensitive)
   * @param {string} [options.filters.status] - Filter by status
   * @param {boolean} [options.filters.is_active] - Filter by active status
   * @param {number} [options.ownerId] - ID of the owner to filter customers by
   * @returns {Promise<{data: Array, pagination: {page: number, limit: number, total: number}}>}
   */
  async getCustomers({ page = 1, limit = 10, filters = {}, ownerId } = {}) {
    try {
      // Prepare query parameters
      const params = {
        skip: (page - 1) * limit,
        limit,
        ...filters
      };
      
      // Add owner_id if provided (matching backend expectation)
      if (ownerId) {
        params.owner_id = ownerId;
      }

      // Clean up params
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === null || params[key] === '') {
          delete params[key];
        }
      });
      
      const response = await api.get('/customers/', { 
        params,
        headers: {
          'X-Total-Count': 'true',
          'Accept': 'application/json'
        }
      });
      
      // Handle different response formats
      let data = [];
      let total = 0;
      
      if (Array.isArray(response.data)) {
        // Direct array response
        data = response.data;
        total = parseInt(response.headers['x-total-count'], 10) || data.length;
      } else if (response.data && Array.isArray(response.data.items)) {
        // Paginated response with items array
        data = response.data.items;
        total = response.data.total || data.length;
      } else if (response.data) {
        // Single object response (shouldn't happen for list endpoint, but handle it)
        data = [response.data];
        total = 1;
      }

      const result = {
        data,
        pagination: {
          page: page,
          limit: limit,
          total: total,
          totalPages: Math.ceil(total / limit) || 1
        }
      };
      
      return result;
      
    } catch (error) {
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
        console.error('Response headers:', error.response.headers);
        
        // Handle 401 Unauthorized (token expired or invalid)
        if (error.response.status === 401) {
          // Clear any existing auth data
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('user');
          
          // Redirect to login with a return URL
          const returnUrl = window.location.pathname + window.location.search;
          window.location.href = `/login?returnUrl=${encodeURIComponent(returnUrl)}`;
          
          throw new Error('Your session has expired. Please log in again.');
        }
        
        // Handle validation errors
        if (error.response.status === 422) {
          const validationErrors = error.response.data.detail || [];
          const formattedErrors = Array.isArray(validationErrors) 
            ? validationErrors.reduce((acc, curr) => {
                const field = curr.loc?.[curr.loc.length - 1] || 'general';
                return { ...acc, [field]: curr.msg };
              }, {}) 
            : { general: 'Invalid data provided' };
          const validationError = new Error('Validation error');
          validationError.validation = formattedErrors;
          throw validationError;
        }
        
        // Handle other error statuses
        const errorMessage = error.response.data?.detail || 
                            error.response.data?.message || 
                            error.response.statusText || 
                            'An error occurred while processing your request';
        throw new Error(errorMessage);
      } else if (error.request) {
        // The request was made but no response was received
        console.error('No response received:', error.request);
        throw new Error('Unable to connect to the server. Please check your internet connection and try again.');
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Request setup error:', error.message);
        throw new Error('An error occurred while setting up the request. Please try again.');
      }
    }
  },

  // Get a single customer by ID
  async getCustomer(id) {
    try {
      const response = await api.get(`/customers/${id}`);
      return response.data;
    } catch (error) {
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
        console.error('Response headers:', error.response.headers);
        
        // Handle 401 Unauthorized (token expired or invalid)
        if (error.response.status === 401) {
          // Clear any existing auth data
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('user');
          
          // Redirect to login with a return URL
          const returnUrl = window.location.pathname + window.location.search;
          window.location.href = `/login?returnUrl=${encodeURIComponent(returnUrl)}`;
          
          throw new Error('Your session has expired. Please log in again.');
        }
        
        // Handle validation errors
        if (error.response.status === 422) {
          const validationErrors = error.response.data.detail || [];
          const formattedErrors = Array.isArray(validationErrors) 
            ? validationErrors.reduce((acc, curr) => {
                const field = curr.loc?.[curr.loc.length - 1] || 'general';
                return { ...acc, [field]: curr.msg };
              }, {}) 
            : { general: 'Invalid data provided' };
          const validationError = new Error('Validation error');
          validationError.validation = formattedErrors;
          throw validationError;
        }
        
        // Handle other error statuses
        const errorMessage = error.response.data?.detail || 
                            error.response.data?.message || 
                            error.response.statusText || 
                            'An error occurred while processing your request';
        throw new Error(errorMessage);
      } else if (error.request) {
        // The request was made but no response was received
        console.error('No response received:', error.request);
        throw new Error('Unable to connect to the server. Please check your internet connection and try again.');
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Request setup error:', error.message);
        throw new Error('An error occurred while setting up the request. Please try again.');
      }
    }
  },

  // Create a new customer with support for file uploads
  async createCustomer(formData) {
    try {
      // Log form data for debugging
      console.log('[CustomerService] Creating customer with form data');
      
      // Make sure we're working with a FormData object
      if (!(formData instanceof FormData)) {
        throw new Error('Expected FormData object as parameter');
      }
      
      // Log form data entries (for debugging, remove in production)
      const formDataObj = {};
      for (let pair of formData.entries()) {
        if (pair[0] !== 'company_logo' && pair[0] !== 'logo') { // Don't log file content
          console.log(pair[0] + ': ', pair[1]);
          formDataObj[pair[0]] = pair[1];
        }
      }
      
      console.log('[CustomerService] Form data object:', formDataObj);
      
      try {
        const response = await api.post('/customers/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        console.log('[CustomerService] Customer created successfully');
        return response.data;
      } catch (error) {
        console.error('[CustomerService] API Error:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          config: {
            url: error.config?.url,
            method: error.config?.method,
            headers: error.config?.headers,
          }
        });
        throw error;
      }
    } catch (error) {
      console.error('[CustomerService] Error creating customer:', error);
      
      // Format validation errors for the form
      if (error.response?.status === 422 && error.response?.data?.detail) {
        const validationError = new Error('Validation error');
        validationError.validation = {};
        
        // Format validation errors in a way that matches our form's error state
        error.response.data.detail.forEach(err => {
          const field = err.loc?.[1] || 'general';
          validationError.validation[field] = err.msg;
        });
        
        throw validationError;
      }
      
      // For 401 errors, let the API interceptor handle the token refresh
      // We'll just rethrow the error with a user-friendly message
      if (error.response?.status === 401) {
        const authError = new Error('Your session has expired. Please log in again.');
        authError.isAuthError = true;
        throw authError;
      }
      
      // Handle other error responses
      let errorMessage = 'Failed to create customer';
      if (error.response?.data?.detail) {
        errorMessage = Array.isArray(error.response.data.detail) 
          ? error.response.data.detail.map(err => err.msg || JSON.stringify(err)).join('\n')
          : error.response.data.detail;
      } else if (error.response?.statusText) {
        errorMessage = `${error.response.status}: ${error.response.statusText}`;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      const formattedError = new Error(errorMessage);
      formattedError.originalError = error;
      throw formattedError;
    }
  },

  // Update an existing customer with support for file uploads
  async updateCustomer(id, formData) {
    try {
      const isFormData = formData instanceof FormData;
      const config = {
        headers: {
          'Content-Type': isFormData ? 'multipart/form-data' : 'application/json'
        }
      };
      
      const response = await api.put(`/customers/${id}`, formData, config);
      return response.data;
    } catch (error) {
      console.error(`Error updating customer ${id}:`, error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw error;
    }
  },

  /**
   * Delete a customer (soft delete)
   * @param {number|string} id - The ID of the customer to delete
   * @returns {Promise<Object>} The deleted customer data
   * @throws {Error} If the deletion fails
   */
  async deleteCustomer(id) {
    try {
      const response = await api.delete(`/customers/${id}`, {
        validateStatus: (status) => status < 500, // Don't throw for 4xx errors
      });
      
      // Handle successful response
      if (response.status === 200) {
        // Check if the customer was soft-deleted (is_active should be false)
        if (response.data && response.data.is_active === false) {
          return {
            success: true,
            data: response.data,
            message: 'Customer deactivated successfully.'
          };
        }
        return {
          success: true,
          data: response.data,
          message: 'Customer deleted successfully.'
        };
      }
      
      // Handle specific error responses
      if (response.status === 404) {
        throw new Error('Customer not found or you do not have permission to delete it.');
      } else if (response.status === 403) {
        throw new Error('You do not have permission to delete this customer.');
      } else if (response.status === 400) {
        throw new Error(response.data.detail || 'Invalid request.');
      } else {
        throw new Error(response.data?.detail || 'Failed to delete customer. Please try again.');
      }
    } catch (error) {
      console.error(`Error deleting customer ${id}:`, error);
      throw error.response?.data?.detail || error.message || 'An error occurred while deleting the customer.';
    }
  },

  /**
   * Check if a customer with the given email already exists
   * @param {string} email - The email to check
   * @returns {Promise<boolean>} True if email exists, false otherwise
   */
  async checkEmailExists(email) {
    try {
      const response = await api.get(`/customers/check-email?email=${encodeURIComponent(email)}`);
      return response.data.exists; // Return the actual exists value from the response
    } catch (error) {
      console.error('Error checking email:', error);
      // If there's an error, we'll assume the email doesn't exist to be safe
      return false;
    }
  },

  /**
   * Get list of countries from the backend
   * @returns {Promise<Array<{code: string, name: string}>>} List of countries with code and name
   */
  async getCountries() {
    try {
      return api.get('/common/countries')
        .then(response => response.data)
        .catch(error => {
          console.error('Error fetching countries:', error);
          throw error;
        });
    } catch (error) {
      console.error('Error fetching countries:', error);
      // Return a default list if the API call fails
      return [
        { code: 'US', name: 'United States' },
        { code: 'CA', name: 'Canada' },
        { code: 'GB', name: 'United Kingdom' },
        { code: 'AU', name: 'Australia' },
        { code: 'IN', name: 'India' },
      ];
    }
  },

  /**
   * Get available users that can be assigned to a customer
   * @param {number} customerId - The ID of the customer
   * @returns {Promise<Array>} List of available users
   */
  async getAvailableUsers(customerId) {
    try {
      const response = await api.get(`/customers/${customerId}/available-users`);
      console.log("Available users response:", response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching available users:', error);
      throw error;
    }
  },

  /**
   * Assign a user to a customer
   * @param {number} customerId - The ID of the customer
   * @param {number} userId - The ID of the user to assign
   * @returns {Promise<Object>} The updated customer
   */
  async assignUser(customerId, userId) {
    try {
      const response = await api.post(`/customers/${customerId}/assign-user/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error assigning user:', error);
      throw error;
    }
  },
};

export default customerService;
